import { useEffect, useState, useCallback } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = ["new", "contacted", "in-progress", "closed"];

export default function AdminIntakes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => api.get("/admin/intakes").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.put(`/admin/intakes/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const download = async (fileId, filename) => {
    try {
      const res = await api.get(`/admin/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="admin-intakes">
      <h1 className="font-serif text-4xl text-espresso">Career Intakes</h1>
      <p className="text-espresso/60 mt-2">{items.length} total</p>

      <div className="mt-8 space-y-4">
        {items.length === 0 && <p className="text-espresso/50">No intakes yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="bg-taupe rounded-2xl p-6" data-testid={`intake-row-${it.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-serif text-xl text-espresso">{it.full_name}</h3>
                <p className="text-espresso/70 text-sm">{it.email} {it.phone && `· ${it.phone}`}</p>
                <p className="text-espresso/60 text-sm mt-1"><span className="text-champagne">{it.service}</span>{it.target_role && ` · ${it.target_role}`}</p>
                <p className="text-espresso/50 text-xs mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </div>
              <select value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} data-testid={`intake-status-${it.id}`}
                className="bg-ivory border border-champagne/40 rounded-lg px-3 py-2 text-sm text-espresso">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {(it.documents || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {it.documents.map((d) => (
                  <button key={d.id} onClick={() => download(d.id, d.original_filename)} data-testid={`intake-doc-${d.id}`}
                    className="inline-flex items-center gap-2 bg-ivory border border-champagne/40 rounded-full px-4 py-2 text-sm text-espresso hover:border-champagne transition-colors">
                    <FileText size={15} className="text-champagne" /> {d.original_filename} <Download size={15} />
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setOpen(open === it.id ? null : it.id)} className="text-champagne text-sm mt-3 hover:text-espresso transition-colors">
              {open === it.id ? "Hide details" : "View goals"}
            </button>
            {open === it.id && (
              <div className="mt-4 text-sm text-espresso/80 border-t border-champagne/30 pt-4 space-y-2">
                <p><span className="text-espresso/50">Career goals:</span> {it.career_goals}</p>
                {it.current_situation && <p><span className="text-espresso/50">Current situation:</span> {it.current_situation}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
